import { Controller } from "@hotwired/stimulus"

// Étape « Quand ? » d'une séance en groupe : recompose, à chaque frappe, la
// phrase que verront les clients et les premières dates. Mêmes mots que
// ConsultationGroupe#calendrier_en_clair, côté serveur :
//   « Les mardis et jeudis à 18:00, 1 h, à partir du 3 novembre · 8 places · 80 TND les 4 séances »
export default class extends Controller {
  static targets = ["rythme", "jour", "debut", "fin", "heure", "places", "limite", "phrase", "dates"]
  static values  = { duree: String, prix: String, cycle: Number, aujourdhui: String }

  static PLURIELS = { 1: "lundis", 2: "mardis", 3: "mercredis", 4: "jeudis", 5: "vendredis", 6: "samedis", 0: "dimanches" }
  static ORDRE = [1, 2, 3, 4, 5, 6, 0]
  static HEURE = /^([01]\d|2[0-3]):[0-5]\d$/

  connect() { this.recomposer() }

  recomposer() {
    const rythme = this.rythmeTargets.find(r => r.checked)?.value
    const jours  = this.jourTargets.filter(j => j.checked).map(j => Number(j.value))
    const debut  = this.#date(this.debutTarget.value)
    const fin    = this.#date(this.finTarget.value)
    const heure  = this.heureTarget.value
    // « Illimité » choisi, ou le nombre laissé vide : pas de limite (comme le serveur).
    const illimite = this.limiteTargets.find(r => r.checked)?.value === "1" || this.placesTarget.value.trim() === ""
    const places = Number(this.placesTarget.value)

    const calendrier = this.#calendrier(rythme, jours, debut, fin, heure)
    // La consigne vit à part, sur la ligne des dates : collée aux faits, elle
    // donnait « Choisissez une date et une heure · 8 places · Gratuit ».
    const morceaux = calendrier ? [calendrier] : []
    if (illimite) morceaux.push("places illimitées")
    else if (places >= 1) morceaux.push(`${places} place${places > 1 ? "s" : ""}`)
    if (this.prixValue) morceaux.push(this.prixValue)
    // Espace insécable avant « · » : le point ne commence jamais une ligne.
    const phrase = morceaux.join("\u00A0· ")
    this.phraseTarget.textContent = phrase.charAt(0).toUpperCase() + phrase.slice(1)

    this.datesTarget.textContent = calendrier
      ? this.#premieresDates(rythme, jours, debut, fin)
      : "Choisissez une date et une heure pour voir les dates."
  }

  #calendrier(rythme, jours, debut, fin, heure) {
    if (!debut || !this.constructor.HEURE.test(heure)) return null

    if (rythme === "date_unique") {
      return `Le ${this.#format(debut, { weekday: "long", day: "numeric", month: "long" })} à ${heure}, ${this.dureeValue}`
    }

    const noms = this.constructor.ORDRE.filter(j => jours.includes(j)).map(j => this.constructor.PLURIELS[j])
    if (noms.length === 0) return null

    let phrase = `Les ${this.#enumerer(noms)} à ${heure}, ${this.dureeValue}`
    if (debut > this.#date(this.aujourdhuiValue)) phrase += `, à partir du ${this.#format(debut, { day: "numeric", month: "long" })}`
    if (fin) phrase += `, jusqu'au ${this.#format(fin, { day: "numeric", month: "long" })}`
    return phrase
  }

  // « Premier cycle : mar. 4 nov., jeu. 6 nov., … » ou « Premières dates : … ».
  #premieresDates(rythme, jours, debut, fin) {
    if (rythme === "date_unique") return ""

    const combien = this.cycleValue > 0 ? this.cycleValue : 4
    const dates = []
    const jour = new Date(debut)
    for (let i = 0; i < 400 && dates.length < combien; i++) {
      if (fin && jour > fin) break
      if (jours.includes(jour.getDay())) dates.push(new Date(jour))
      jour.setDate(jour.getDate() + 1)
    }
    if (dates.length === 0) return "Aucune séance entre ces deux dates."

    const liste = dates.map(d => this.#format(d, { weekday: "short", day: "numeric", month: "short" })).join(", ")
    if (this.cycleValue > 0) {
      return dates.length < this.cycleValue
        ? `Pas assez de semaines pour un cycle complet de ${this.cycleValue} séances : repoussez la dernière séance.`
        : `Premier cycle : ${this.#point(liste)}`
    }
    return `Premières dates : ${this.#point(liste)}`
  }

  // « nov. » porte déjà son point : on n'en ajoute pas un second.
  #point(texte) { return texte.endsWith(".") ? texte : `${texte}.` }

  #enumerer(noms) {
    if (noms.length === 1) return noms[0]
    return `${noms.slice(0, -1).join(", ")} et ${noms[noms.length - 1]}`
  }

  // Une date « AAAA-MM-JJ » lue en heure locale, sans glissement de fuseau.
  #date(valeur) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valeur || "")
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
  }

  #format(date, options) {
    return new Intl.DateTimeFormat("fr-FR", options).format(date)
  }
}
